import React, { useMemo, useRef } from 'react';
import { getPercentageInfo, sortMapAuxiliaryFunc } from '@op/utils';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import { chartAllColors } from '@op/constants';
import { formatTimeToShow } from '@op/utils/time';
import { percentageFormat, reviewsFormat } from '@op/utils/value';
import { useIntl } from 'umi';

export default function PieChart({
  data,
  title = '',
  height = 330,
  filter,
  translate = (value: string) => value,
  sortMap,
  colorMap = {},
  selected = '',
  eventsDict = null,
}: any) {
  const eChartsRef = useRef(null as any);
  const { formatMessage } = useIntl();
  const options = useMemo(() => {
    const legendData: Array<any> = [];
    let mentionsTotal = 0;
    const newCharData = data?.map(
      (v: { name: string; value: number }, index: number) => {
        const color = colorMap[v?.name] || chartAllColors[index];
        legendData.push({
          name: v?.name,
          icon: 'circle',
          itemStyle: {
            color: color,
          },
        });
        mentionsTotal += v.value || 0;

        return {
          ...v,
          selected: selected === v?.name,
          itemStyle: {
            color: color,
          },
        };
      },
    );
    if (sortMap) {
      legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
      newCharData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
    }
    const options = {
      legend: {
        data: legendData,
        formatter: translate,
      },
      tooltip: {
        borderWidth: 0,
        borderRadius: 6,
        padding: 0,
        formatter: (params: any) => {
          return formatChartParamsDate({
            title: formatTimeToShow(filter),
            rightTitle: title,
            params,
            listTitleFunc(item) {
              return translate(item.name);
            },
            valueFormat(value, element) {
              return `${reviewsFormat(value)}(${percentageFormat(
                element.percent,
              )})`;
            },
          });
        },
      },
      title: [
        {
          text: '{name|' + reviewsFormat(mentionsTotal) + '}',
          top: '44%',
          left: 'center',
          textStyle: {
            rich: {
              name: {
                fontSize: 24,
                fontFamily: 'num-m',
                color: '#000000',
                padding: [10, 0],
              },
            },
          },
        },
      ],
      series: [
        {
          selectedMode: !!eventsDict,
          labelLayout: (params: any) => {
            const isLeft =
              params.labelRect.x <
              eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
            const points = params.labelLinePoints;
            // Update the end point.
            if (!points?.[2]?.[0]) return {};
            points[2][0] = isLeft
              ? params.labelRect.x
              : params.labelRect.x + params.labelRect.width;
            return {
              labelLinePoints: points,
            };
          },
          label: {
            color: 'auto',
            formatter: (params: any) => {
              let percent = mentionsTotal
                ? getPercentageInfo(params?.value, mentionsTotal, 2)
                : `${params.percent.toFixed(2)}%`;
              return (
                '{value| ' +
                translate(params.name) +
                ' }：{a|' +
                reviewsFormat(params.value) +
                '(' +
                percent +
                ')}\n{hr|}\n{a|}'
              );
            },
            edgeDistance: 10,
            rich: {
              a: {
                color: '#262832',
                lineHeight: 20, //设置最后一行空数据高度，为了能让延长线与hr线对接起来
                align: 'center',
              },
              hr: {
                //设置hr是为了让中间线能够自适应长度
                borderColor: 'auto', //hr的颜色为auto时候会主动显示颜色的
                width: '105%',
                borderWidth: 0.5,
                height: 0.5,
              },
              value: {
                color: '#262832',
              },
              per: {
                //用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
                padding: [4, 0],
              },
            },
          },
          data: newCharData || [],
        },
      ],
    };

    return options;
  }, [data, formatMessage]);

  return (
    <EChartCommon
      empty={!data || data.length === 0}
      optionsProps={{
        type: 'pie',
        pieLabelAlignTo: 'edge',
      }}
      ref={eChartsRef}
      options={options}
      height={height}
      eventsDict={eventsDict}
    />
  );
}
